// src/components/PushNotificationListener.jsx
import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import usePushNotification from '../hooks/usePushNotification'

/**
 * Mounts the FCM/APNs push-notification listener globally so that messages
 * forwarded from the native iOS / Android WebView (`window.handleYumExpressFCM`
 * or `window.postMessage({ source: 'yumexpress-fcm', ... })`) are turned into
 * in-app toasts AND wired into the rest of the app:
 *
 *   - Order tracking page refetches its order detail / live timeline.
 *   - The notification bell badge re-pulls unread count.
 *   - The orders list refreshes so "active" orders update statuses live.
 *
 * Renders nothing; side-effect only.
 */
const PushNotificationListener = () => {
  const qc = useQueryClient()

  /**
   * Invalidate every cache that an order-related push might affect. Safe to
   * over-invalidate — React Query dedupes refetches and the user only feels
   * the freshness, not the work.
   */
  const refreshOrderCaches = useCallback(
    orderId => {
      // Order tracking page (single order detail) — both common shapes.
      if (orderId) {
        qc.invalidateQueries({ queryKey: ['order', orderId] })
        qc.invalidateQueries({ queryKey: ['order-tracking', orderId] })
        qc.invalidateQueries({ queryKey: ['order-status', orderId] })
        qc.invalidateQueries({ queryKey: ['order-reviews', orderId] })
        qc.invalidateQueries({ queryKey: ['order-timeline', orderId] })
        qc.invalidateQueries({ queryKey: ['order-eta', orderId] })
      }
      qc.invalidateQueries({ queryKey: ['customer-order-history'] })
      // Lists / dashboards.
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['active-orders'] })
      qc.invalidateQueries({ queryKey: ['order-history'] })
      // Notification bell / panel.
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notifications-unread'] })
    },
    [qc]
  )

  usePushNotification({
    onDeliveryUpdate: data => {
      refreshOrderCaches(data?.order_id)
    },
    onPromo: () => {
      // Promos don't touch order caches, but make sure the bell badge updates.
      qc.invalidateQueries({ queryKey: ['notifications-unread'] })
      qc.invalidateQueries({ queryKey: ['notifications'] })
    },
    onGeneral: data => {
      // General notifications can still carry an order_id (e.g. payment received).
      refreshOrderCaches(data?.order_id)
    }
  })

  return null
}

export default PushNotificationListener
